import React from "react"

export type ColorMode = "light" | "dark"

const STORAGE_KEY = "ui-theme"

type ColorModeContextValue = {
	mode: ColorMode
	setMode: (mode: ColorMode) => void
	toggleColorMode: () => void
}

const ColorModeContext = React.createContext<ColorModeContextValue | undefined>(
	undefined
)

function safeGetInitialMode(): ColorMode {
	// localStorage -> system -> light
	if (typeof window === "undefined") return "light"

	const saved = window.localStorage.getItem(STORAGE_KEY)
	if (saved === "light" || saved === "dark") return saved

	const prefersDark =
		typeof window.matchMedia === "function" &&
		window.matchMedia("(prefers-color-scheme: dark)").matches

	return prefersDark ? "dark" : "light"
}

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [mode, setModeState] = React.useState<ColorMode>(safeGetInitialMode)

	const setMode = React.useCallback((next: ColorMode) => {
		setModeState(next)
	}, [])

	const toggleColorMode = React.useCallback(() => {
		setModeState((m) => (m === "light" ? "dark" : "light"))
	}, [])

	React.useEffect(() => {
		try {
			window.localStorage.setItem(STORAGE_KEY, mode)
		} catch {
			// ignore storage errors
		}
		// Allow non-MUI CSS to follow the user's explicit choice (not only OS setting)
		document.documentElement.dataset.theme = mode
	}, [mode])

	return (
		<ColorModeContext.Provider value={{ mode, setMode, toggleColorMode }}>
			{children}
		</ColorModeContext.Provider>
	)
}

export function useColorMode(): ColorModeContextValue {
	const ctx = React.useContext(ColorModeContext)
	if (!ctx) {
		throw new Error("useColorMode must be used within ColorModeProvider")
	}
	return ctx
}


